/* eslint-disable react-hooks/rules-of-hooks */
import { useEffect, useCallback } from "react";
import {
  Outlet,
  createFileRoute,
  useNavigate,
  useRouterState,
  redirect,
} from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import Cookies from "js-cookie";

import {
  routes,
  token,
  refreshToken,
  ApiRoutes,
  AUTH_STORAGE_KEYS,
} from "../constants";
import Sidebar from "../components/layout/Sidebar";
import TopBar from "../components/layout/TopBar";
import SupportButton from "../components/layout/SupportButton";
import { AppRoutes, matchRouteByPathname } from "../utils";
import { buildNavSections } from "../config/navigation";
import { useSidebar } from "../hooks/useSidebar";
import { LOGOUT, useAppState } from "../contexts/AppContext";
import { apiClient } from "../services/api";

function getPageTitle(pathname: string) {
  const currentRoute = matchRouteByPathname(routes, pathname);

  // The dashboard renders its own personalized greeting hero, so the shell
  // suppresses its default heading there.
  return currentRoute?.path === AppRoutes.DASHBOARD
    ? null
    : currentRoute?.title;
}

export const Route = createFileRoute("/_app")({
  notFoundComponent: () => {
    return <p>The Page you requested was not found</p>;
  },
  beforeLoad: () => {
    // Check if token exists in cookies, not just user in state
    // This prevents redirect during initial state load
    const authToken = Cookies.get(token);

    if (!authToken) {
      throw redirect({ to: AppRoutes.LOGIN });
    }
  },
  component: () => {
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const {
      dispatch,
      state: { user, business, businessUserRole },
    } = useAppState();

    const sidebar = useSidebar();

    const {
      location: { pathname },
    } = useRouterState();

    const navSections = buildNavSections(
      business?.businessType,
      businessUserRole,
    );

    const handleLogout = useCallback(async () => {
      try {
        await apiClient.post({
          url: ApiRoutes.LOGOUT,
        });
      } catch (error) {
        console.error("Logout API failed:", error);
      } finally {
        Cookies.remove(token);
        Cookies.remove(refreshToken);
        queryClient.removeQueries();
        // Clear only auth-owned keys — UI prefs (e.g. sidebar) survive
        AUTH_STORAGE_KEYS.forEach((key) => localStorage.removeItem(key));
        sessionStorage.clear();
        dispatch({ type: LOGOUT });
        // Small delay to ensure state is cleared before navigation
        setTimeout(() => {
          navigate({ to: AppRoutes.LOGIN, replace: true });
        }, 0);
      }
    }, [dispatch, navigate, queryClient]);

    // close the sidebar on mobile when the user navigates to a new page
    const { closeMobile } = sidebar;
    useEffect(() => {
      closeMobile();
    }, [pathname, closeMobile]);

    const mainMargin =
      sidebar.mode === "drawer"
        ? ""
        : sidebar.mode === "rail"
        ? "md:ml-[4.5rem]"
        : "md:ml-64";

    return (
      <div className="app-layout min-h-screen bg-appbg">
        <Sidebar
          mode={sidebar.mode}
          mobileOpen={sidebar.mobileOpen}
          onCloseMobile={sidebar.closeMobile}
          onToggleCollapsed={sidebar.toggleCollapsed}
          sections={navSections}
          pathname={pathname}
        />

        <div
          className={`flex flex-col min-h-screen transition-[margin] duration-300 ${mainMargin}`}
        >
          <TopBar
            pathname={pathname}
            user={user}
            business={business}
            onOpenMobileNav={sidebar.openMobile}
            onSignOut={handleLogout}
            showHamburger={sidebar.mode === "drawer"}
          />

          <main className="px-4 md:px-6 py-6 flex flex-col grow">
            {getPageTitle(pathname) && (
              <h1 className="text-2xl mb-8">{getPageTitle(pathname)}</h1>
            )}
            <Outlet />
          </main>
        </div>

        <SupportButton />
      </div>
    );
  },
});
